import { gdpr, gdprCom } from '@umu/umu-prompt';
import { Root } from 'react-dom/client';
import {
    isUMUWebview,
    isIos,
    isInIframe,
    isInApp,
    isMobile,
    isMobileDomain,
    isInMobile,
    isInMicroApp,
} from '@umu/umu-ua';
import { getData } from '@umu/umu-util';
import * as imageApi from '@umu/umu-image-handle';
import { IPageData, PlatePermissionType } from '@umu/umu-types';
import * as commonHeader from '@umu/common-header';
import { initBodyClassName, isSystem, isProd } from '@umu/ukit-lang';
import { $window, loadScriptAsync, openWindow, redirectTo, setTitle } from '@umu/umu-dom';
import { getGdprBootJsUrl } from '@umu/umu-mobile-layout';
import { renderFullWatermark } from '@umu/umu-full-watermark';
import AlertDialog from 'common/src/components/modalDialog/alert';
import { getLoginUrl, checkXFrameAllow, isInMiniProgram } from '../@utils';
import userAchievementService from '../components/user/@userAchievement/userAchievementService';
import { initSentry } from './sentry';
interface IPageConfig {
    disableSentry?: boolean;
}

import 'uno.css';
class Page<T extends IPageData> {
    constructor(private data: T, private config?: IPageConfig) {
        this.init();
    }

    private isDebug() {
        return location.href.includes('app.') || /debugger=1/i.test($window.location.search);
    }

    private goLogin() {
        const loginUrl = window.location.host.indexOf('m.') === 0 ? '/globaluser/loginpage' : '/auth/login';
        redirectTo(loginUrl);
    }

    private registerSentry() {
        if (isProd || (!isProd && this.isDebug())) {
            const umuId = getData(this.data, 'userInfo', 'umu_id') || '';
            initSentry(umuId);
        }
    }

    public initGdprCom() {
        if (isUMUWebview) {
            return;
        }
        if (isInIframe()) {
            return;
        }
        const userId = getData(this.data, 'userInfo', 'student_id') || '0';
        const lang = getData(this.data, 'Lang');
        // 不好拿到 IS_PRODUCTION，先 hack 处理一下
        let isProduction = true;
        isProduction = false;
        const gdprUrl = getGdprBootJsUrl(isProduction, this.data.SYSTEM);
        gdprCom({
            userId,
            platform: window.location.host.indexOf('m.') === 0 ? 'm' : 'web',
            lang,
            disableDeclineAlways: this.data.gdprConfig && this.data.gdprConfig.disableDeclineAlways,
            disableAgreeFlush: this.data.gdprConfig && this.data.gdprConfig.disableAgreeFlush,
            onAgree: () => {
                loadScriptAsync([gdprUrl]);
            },
            onDecline: () => {
                if (!this.data.gdprConfig || !this.data.gdprConfig.disableDefaultDecline) {
                    this.goLogin();
                }
            },
        });
    }

    public initGdpr() {
        if (isUMUWebview) {
            return;
        }
        if (isInIframe()) {
            return;
        }
        const userId = getData(this.data, 'userInfo', 'student_id') || '';
        const lang = getData(this.data, 'Lang');

        gdpr({
            userId,
            platform: window.location.host.indexOf('m.') === 0 ? 'm' : 'web',
            lang,
        });
    }

    public setupFetchErrorListener() {
        window.addEventListener('message', (event) => {
            if (event.origin !== window.location.origin) {
                return;
            }

            if (event.data.type !== 'fetch-alert-message') {
                return;
            }

            const { title, content, btnConfirmText, gotoLoginPageOnConfirmClicked, errorCode } = event.data;
            if (!title || !content || !btnConfirmText) {
                return;
            }

            if (errorCode === 20201) {
                return AlertDialog({
                    title: '',
                    content,
                    className: 'access-time-dialog',
                    btnConfirmText,
                    submitBtnClick: () => {
                        openWindow('/access-denied/access-time-limit', '_self');
                    },
                });
            }

            const submitBtnClick = gotoLoginPageOnConfirmClicked
                ? () => {
                      window.location.href = getLoginUrl();
                  }
                : () => {
                      /** */
                  };

            AlertDialog({
                title,
                content,
                showClose: true,
                btnConfirmText,
                submitBtnClick,
            });
            return;
        });
    }

    /**
     * 动态加载vconsole
     */
    public initVconsole() {
        if (this.isDebug()) {
            loadScriptAsync(['https://statics-cdn-cn.umucdn.cn/common/vConsole/v3.15.1/vconsole.min.js']).then(() => {
                // tslint:disable-next-line: no-unused-expression
                window.VConsole && new window.VConsole();
            });
        }
    }

    public fisIOS12KeyboardBug() {
        // https://juejin.im/post/5c07442f51882528c4469769
        if (!isIos) {
            return;
        }
        document.addEventListener(
            'blur',
            (e: any) => {
                if (['input', 'textarea'].indexOf(e.target.localName) !== -1) {
                    document.documentElement.scrollTop = document.documentElement.scrollTop;
                    document.body.scrollTop = document.body.scrollTop;
                }
            },
            true,
        );
    }

    public loadPingppSync() {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://statics-umu-cn.umucdn.cn/cdn/js/pay/pingpp.2.2.1.js';
        document.body.appendChild(script);
    }

    public disableMobileFixedLayerWhenKeyboardAppears() {
        // mobile设备上，有focus事件弹起键盘后，去除doc-fixed效果
        if (!isMobile) {
            return;
        }
        let timer = 0;
        // 支持处理的元素父容器选择器列表
        const list = ['comment-reply-poster', 'comment-edit-poster', 'video-comment-main-poster'];
        const isMatchParent = (e: any) => {
            const path: any[] = e.path || (e.composedPath && e.composedPath()) || [];
            const hasClass = (el: any, cls: string) => {
                if (!el) {
                    return false;
                }
                // 优先使用 classList（对 SVG/HTMLElement 都更稳）
                if (el.classList && typeof el.classList.contains === 'function') {
                    return el.classList.contains(cls);
                }
                // HTML 元素一般为 string
                const className = el.className;
                if (typeof className === 'string') {
                    return className.indexOf(cls) !== -1;
                }
                // SVGElement 的 className 可能是 SVGAnimatedString（{ baseVal: string }）
                if (className && typeof className.baseVal === 'string') {
                    return className.baseVal.indexOf(cls) !== -1;
                }
                return false;
            };
            const ret = path.some((el) => {
                return list.some((selector) => {
                    return hasClass(el, selector);
                });
            });
            return ret;
        };
        const handlerIn = (e: any) => {
            if (!isMatchParent(e)) {
                return;
            }
            const noDocFixedCls = 'no-doc-fixed-switch';
            if (document.body.className.indexOf(noDocFixedCls) === -1) {
                document.body.className += ' ' + noDocFixedCls;
            }
        };
        const handlerOut = () => {
            const noDocFixedRegexp = new RegExp('\\s' + 'no\\-doc\\-fixed\\-switch', 'g');
            document.body.className = document.body.className.replace(noDocFixedRegexp, '');
        };

        document.addEventListener(
            'focusin',
            (e: any) => {
                clearTimeout(timer);
                handlerIn(e);
            },
            false,
        );
        document.addEventListener(
            'focusout',
            () => {
                clearTimeout(timer);
                timer = $window.setTimeout(() => {
                    handlerOut();
                }, 300);
            },
            false,
        );
    }

    public checkXFrameAllow() {
        checkXFrameAllow();
    }

    /**
     * 初始化判断是否需要 USE 的header
     */
    public initUSEHeader() {
        // 在上层手动控制是否展示 use header
        if ((this.data as any)?.disableUSEInject) {
            return;
        }
        if (!(window as any).isInUSE) {
            return;
        }
        if (isInApp()) {
            return;
        }
        if (isInMobile() || isMobileDomain()) {
            return;
        }
        let header = document.querySelector('.use-header');
        if (!header) {
            header = document.createElement('div');
            header.classList.add('use-header');
            document.body.insertBefore(header, document.querySelector('.doc'));
        }
        if (header) {
            const { siteHost, SYSTEM, Lang, userInfo } = this.data;
            const platform_permission = (userInfo as any).platform_permission;
            commonHeader.render({
                hostEl: header as HTMLElement,
                userInfo: userInfo as any,
                enterpriseInfo: userInfo.enterprise_info as any,
                siblingSubHeader: true,
                siteHost: siteHost,
                system: SYSTEM,
                lang: Lang,
                isCAdministrator: false,
                isOnlyUseEnterpriseAccount: platform_permission === PlatePermissionType.USE_ONLY,
                isOnlyUmuEnterpriseAccount: platform_permission === PlatePermissionType.UMU,
                isUseAndUmuEnterpriseAccount: platform_permission === PlatePermissionType.UMU_AND_USE,
            });
        }
    }

    public registerUnmount(root: Root) {
        if (!isInMicroApp || !root) {
            return;
        }

        window.addEventListener('unmount', () => {
            root.unmount();
        });
    }

    public initWeakRefPolyfill() {
        if (typeof (window as any).WeakRef === 'undefined') {
            (window as any).WeakRef = class {
                private value: any;
                constructor(value: any) {
                    this.value = value;
                }
                deref() {
                    return this.value;
                }
            };
        }
    }

    public initFullWatermark() {
        renderFullWatermark();
    }

    public init() {
        if (this.config?.disableSentry !== true) {
            this.registerSentry();
        }

        const isSystemCN = isSystem('CN') === true;
        if (!isSystemCN) {
            if (isSystem('en')) {
                this.initGdprCom();
            } else {
                this.initGdpr();
            }
            imageApi.listen();
        }
        this.initWeakRefPolyfill();
        this.initFullWatermark();
        this.setupFetchErrorListener();
        this.fisIOS12KeyboardBug();
        this.initVconsole();
        this.disableMobileFixedLayerWhenKeyboardAppears();
        isInMiniProgram((is) => {
            if (!is) {
                if (isSystemCN) {
                    this.loadPingppSync();
                }
            }
        });
        /**
         * @desc 解除弹窗微信限制
         */
        userAchievementService.startup();
        this.checkXFrameAllow();

        // 添加initBodyClassName，在body中注入css命名空间
        initBodyClassName();
        this.initUSEHeader();
    }

    public setupPageTitle(title: string) {
        setTitle(title);
    }
}

export default Page;
